import { io, type Socket } from 'socket.io-client';
import type { ClientToServerEvents, ServerToClientEvents } from '@tv-casino/shared';

export type AppSocket = Socket<ServerToClientEvents, ClientToServerEvents>;

let socket: AppSocket | null = null;

/**
 * One socket per tab, created lazily and never torn down. A singleton keeps
 * React StrictMode's double-mount from opening two connections (and, on the
 * host, two rooms). Same-origin in production; in dev the Vite proxy forwards
 * /socket.io to Fastify, so phones only ever need the Vite URL.
 *
 * WebSocket first, but `tryAllTransports` is what makes the fallback real:
 * since socket.io-client 4.7 a failed first transport ends the attempt
 * instead of moving down the list. Phones are the ones that hit it — iCloud
 * Private Relay, carrier proxies and captive Wi-Fi all break the upgrade —
 * and without the flag they never reach long-polling at all.
 */
export function getSocket(): AppSocket {
  if (!socket) socket = io({ transports: ['websocket', 'polling'], tryAllTransports: true });
  return socket;
}

/** A failed ack, carrying the server's error code so callers can branch. */
export class RequestError extends Error {
  code: string;
  constructor(code: string, message: string) {
    super(message);
    this.code = code;
  }
}

/** Promise-wrapped emit, since every request event takes an ack callback. */
export function request<T>(
  emit: (
    cb: (res: { ok: true; data: T } | { ok: false; error: { code: string; message: string } }) => void,
  ) => void,
): Promise<T> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error('The table did not answer. Try again.')), 8000);
    emit((res) => {
      clearTimeout(timer);
      if (res.ok) resolve(res.data);
      else reject(new RequestError(res.error.code, res.error.message));
    });
  });
}
